import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductCard, { ProductCardSkeleton } from './ProductCard';
import { ChevronLeftIcon, ChevronRightIcon } from './icons';

const ITEM = 'w-[68%] shrink-0 snap-start sm:w-[40%] md:w-[31%] lg:w-[calc((100%-3*1.5rem)/4)]';

/**
 * Horizontal, swipeable row of product cards with scroll-snap. On wider
 * screens, previous/next buttons scroll by one view.
 */
export default function ProductRail({ products, loading = false, label, className = '' }) {
  const { t } = useTranslation();
  const scroller = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const update = () =>
      setEdges({
        start: el.scrollLeft <= 4,
        end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
      });
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [products, loading]);

  const scrollBy = (dir) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  const arrow =
    'hidden h-11 w-11 items-center justify-center rounded-full border border-border bg-background text-ink transition hover:border-ink disabled:pointer-events-none disabled:opacity-30 md:inline-flex';

  return (
    <div className={`relative ${className}`}>
      <ul
        ref={scroller}
        aria-label={label}
        className="no-scrollbar -mx-4 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:scroll-px-6 sm:px-6 lg:mx-0 lg:scroll-px-0 lg:gap-6 lg:px-0"
      >
        {loading
          ? [0, 1, 2, 3].map((i) => (
              <li key={i} className={ITEM}>
                <ProductCardSkeleton />
              </li>
            ))
          : products.map((p) => (
              <li key={p.id} className={ITEM}>
                <ProductCard product={p} sizes="(min-width: 1024px) 23vw, (min-width: 640px) 40vw, 68vw" />
              </li>
            ))}
      </ul>
      {!loading && products.length > 4 && (
        <div className="mt-5 hidden justify-end gap-2 md:flex">
          <button type="button" className={arrow} onClick={() => scrollBy(-1)} disabled={edges.start}>
            <ChevronLeftIcon className="h-5 w-5" />
            <span className="sr-only">{t('common.previous')}</span>
          </button>
          <button type="button" className={arrow} onClick={() => scrollBy(1)} disabled={edges.end}>
            <ChevronRightIcon className="h-5 w-5" />
            <span className="sr-only">{t('common.next')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
